import { useCallback, useRef } from 'react';

type ContestSelectorItem = {
  id: string;
  label: string;
};

type ContestSelectorProps = {
  items: ContestSelectorItem[];
  selectedId: string;
  onSelect: (id: string) => void;
};

const pillClasses = (isSelected: boolean) =>
  `shrink-0 rounded-full px-4 py-2 text-[12px] font-semibold transition-colors ${
    isSelected
      ? 'border border-white/60 bg-white/10 text-white'
      : 'border border-transparent bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
  }`;

const pillStyle = { fontFamily: 'Inter, sans-serif' } as const;

export default function ContestSelector({ items, selectedId, onSelect }: ContestSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const setButtonRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) buttonRefs.current.set(id, el);
    else buttonRefs.current.delete(id);
  }, []);

  if (items.length <= 1) return null;

  const [first, ...rest] = items;

  const handleSelect = (id: string) => {
    onSelect(id);

    const container = scrollRef.current;
    if (!container) return;

    if (id === first.id) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    const btn = buttonRefs.current.get(id);
    if (!btn) return;

    // Scroll by however much of the pill is outside the strip, so the last date
    // comes fully into view from its own side instead of being dragged to the
    // left edge — which for the last pill the browser then has to clamp, leaving
    // the strip somewhere neither the pill nor its neighbour is whole.
    // Rectangles rather than `offsetLeft`, which is measured from whichever
    // ancestor happens to be positioned and not necessarily this container.
    const pill = btn.getBoundingClientRect();
    const strip = container.getBoundingClientRect();
    const overflowLeft = pill.left - strip.left;
    const overflowRight = pill.right - strip.right;

    const delta = overflowLeft < 0 ? overflowLeft : overflowRight > 0 ? overflowRight : 0;
    if (delta !== 0) {
      container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
    }
  };

  return (
    <div className="mb-4 flex gap-2">
      <button
        type="button"
        onClick={() => handleSelect(first.id)}
        className={pillClasses(first.id === selectedId)}
        style={pillStyle}
        aria-pressed={first.id === selectedId}
      >
        {first.label}
      </button>
      {/*
        `touch-pan-x` is what keeps the page scrolling with a finger on this
        strip. Android reported that once a date other than the first was
        chosen, the page stopped scrolling: selecting scrolls the strip away
        from its start, and a strip with room to move both ways captures a
        near-vertical swipe as a horizontal one and holds the gesture until the
        finger lifts. Declaring that this element only ever pans horizontally
        sends the vertical part of any swipe to the page instead, and
        `pinch-zoom` keeps the browser's own zoom working over it.

        `overflow-y-hidden` is the same argument for the other axis: `overflow-x`
        alone would compute `overflow-y` to `auto`, quietly making a one-line
        strip a vertical scroll container too.
      */}
      <div
        ref={scrollRef}
        data-test-id="contest-strip"
        className="flex touch-pan-x touch-pinch-zoom gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {rest.map(item => (
          <button
            key={item.id}
            ref={el => setButtonRef(item.id, el)}
            type="button"
            onClick={() => handleSelect(item.id)}
            className={pillClasses(item.id === selectedId)}
            style={pillStyle}
            aria-pressed={item.id === selectedId}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
